// THE DELIVERY CHALLAN -- M4 O9 (owner D189 B2 / D195). Printable only; the
// app e-mails nothing.
//
// ⚠️ THE FENCE (the customer-document rule, hard rule 5): a challan prints NO
// cost, NO OEM price, NO OEM name and NO sell price. Two layers: the server's
// get_delivery_note answers an ALLOWLIST (test_fulfilment.py asserts the exact
// keys), and this preview maps every line through toDnDocLines() into the
// narrow DnDocLine -- whatever else a line object carries never reaches the
// page. DeliveryNotePreview.fence.test.tsx feeds it a POISONED line.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getDeliveryNotePrint, type DeliveryNotePrint } from "@/services/api";
import { formatDate } from "@/lib/format";
import { colors, semanticColors, radius, spacing, heights, typography, layout, shadows } from "@/styles/tokens";

/** The ONLY line shape the challan renders. Adding a field here is a fence change. */
export interface DnDocLine { lineNo: number | null; partNumber: string; description: string; uom: string; hsn: string; qty: number }

export function toDnDocLines(lines: ReadonlyArray<Record<string, unknown>>): DnDocLine[] {
  return lines.map((l) => ({
    lineNo: typeof l.line_no === "number" ? l.line_no : null,
    partNumber: typeof l.part_number === "string" ? l.part_number : "",
    description: typeof l.description === "string" ? l.description : "",
    uom: typeof l.uom === "string" ? l.uom : "",
    hsn: typeof l.hsn_code === "string" ? l.hsn_code : "",
    qty: typeof l.qty === "number" ? l.qty : 0,
  }));
}

function addressLines(a: DeliveryNotePrint["ship_to"]): string[] {
  return [a.name, a.street, a.street2, [a.city, a.zip].filter(Boolean).join(" ") || null, a.state,
    a.vat ? `GSTIN ${a.vat}` : null].filter((x): x is string => !!x);
}

export function DeliveryNoteDocument({ note }: { note: DeliveryNotePrint }) {
  const lines = toDnDocLines(note.lines as unknown as Record<string, unknown>[]);
  const cell: React.CSSProperties = { padding: `${spacing[6]}px ${spacing[8]}px`, borderBottom: `1px solid ${semanticColors.border}`, fontSize: typography.fontSize.sm, textAlign: "left" };
  const issuer = note.issuer;
  return (
    <div data-testid="dn-doc-page" style={{
      width: layout.pdfPageWidth, maxWidth: "100%", minHeight: layout.pdfPageMinHeight, backgroundColor: semanticColors.surface,
      boxShadow: shadows.modal, padding: spacing[40], marginBottom: spacing[24], color: semanticColors.textPrimary,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: spacing[24], flexWrap: "wrap" }}>
        <div data-testid="dn-doc-issuer" style={{ fontSize: typography.fontSize.sm, whiteSpace: "pre-line" }}>
          <div style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold }}>{issuer.company_name || "—"}</div>
          {issuer.address}{issuer.gstin ? `\nGSTIN ${issuer.gstin}` : ""}{issuer.contact_email ? `\n${issuer.contact_email}` : ""}
          {issuer.contact_phone ? `\n${issuer.contact_phone}` : ""}
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold }}>Delivery Challan</div>
          <div data-testid="dn-doc-number" style={{ fontSize: typography.fontSize.lg }}>{note.number}</div>
          <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
            Date {formatDate(note.dn_date)} · Our ref. {note.quote_name}
          </div>
          {note.reverses && <div style={{ color: colors.red600, fontSize: typography.fontSize.sm }}>Reverses {note.reverses}</div>}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: spacing[24], marginTop: spacing[24], fontSize: typography.fontSize.sm }}>
        <div data-testid="dn-doc-customer"><div style={{ color: semanticColors.textMuted }}>Customer</div>
          <div style={{ fontWeight: typography.fontWeight.medium }}>{note.customer.name || "—"}</div>
          {note.customer.vat && <div>GSTIN {note.customer.vat}</div>}</div>
        <div data-testid="dn-doc-shipto"><div style={{ color: semanticColors.textMuted }}>Deliver to</div>
          {addressLines(note.ship_to).map((l, i) => <div key={i}>{l}</div>)}</div>
        <div data-testid="dn-doc-transport"><div style={{ color: semanticColors.textMuted }}>Transport</div>
          <div>E-way bill: {note.eway_bill_no || "—"}</div><div>LR no.: {note.lr_no || "—"}</div>
          <div>Transporter: {note.transporter || "—"}</div><div>Vehicle: {note.vehicle_no || "—"}</div></div>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: spacing[24] }}>
        <thead><tr>{["#", "Part", "Description", "HSN", "UoM", "Qty"].map((h) => (
          <th key={h} style={{ ...cell, color: semanticColors.textMuted, fontWeight: typography.fontWeight.medium }}>{h}</th>))}</tr></thead>
        <tbody>{lines.map((l, i) => (
          <tr key={i} data-testid={`dn-doc-line-${i + 1}`}>
            <td style={cell}>{l.lineNo ?? i + 1}</td><td style={{ ...cell, whiteSpace: "nowrap" }}>{l.partNumber}</td>
            <td style={cell}>{l.description || "—"}</td><td style={cell}>{l.hsn || "—"}</td><td style={cell}>{l.uom || "—"}</td>
            <td style={cell}>{l.qty}</td>
          </tr>))}
        </tbody>
      </table>
      {note.note && <div style={{ marginTop: spacing[16], fontSize: typography.fontSize.sm, whiteSpace: "pre-line" }}>{note.note}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: spacing[40], fontSize: typography.fontSize.sm, gap: spacing[24], flexWrap: "wrap" }}>
        <div>Received the above goods in good condition.<br /><br />Customer's signature &amp; stamp</div>
        <div style={{ textAlign: "right" }}>For {issuer.company_name || "—"}<br /><br />Authorised signatory</div>
      </div>
    </div>
  );
}

export function DeliveryNotePreview({ id, onClose }: { id: number; onClose: () => void }) {
  const [note, setNote] = useState<DeliveryNotePrint | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    void (async () => {
      const res = await getDeliveryNotePrint(id);
      if (!res.ok) { setError(res.error || "The delivery note could not be read."); return; }
      setNote(res.note!);
    })();
  }, [id]);
  useEffect(() => {
    document.body.classList.add("printing-document");
    return () => document.body.classList.remove("printing-document");
  }, []);
  const btn: React.CSSProperties = { height: heights.control, padding: `0 ${spacing[14]}px`, borderRadius: radius.base, cursor: "pointer", fontSize: typography.fontSize.base };
  // A PORTAL: the step's section may collapse when its state changes (the
  // note just created completes it), and must not hide the document with it.
  return createPortal(
    <div data-testid="dn-doc-backdrop" role="dialog" aria-modal="true" aria-label="Delivery challan" style={{
      position: "fixed", inset: 0, backgroundColor: semanticColors.overlay, overflowY: "auto",
      display: "flex", flexDirection: "column", alignItems: "center", zIndex: 1000,
    }}>
      <div data-testid="dn-doc-toolbar" style={{ position: "sticky", top: 0, display: "flex", gap: spacing[8], justifyContent: "flex-end", width: layout.pdfPageWidth, maxWidth: "100%", padding: `${spacing[10]}px 0` }}>
        <button type="button" data-testid="dn-doc-print" disabled={!note} onClick={() => window.print()}
          style={{ ...btn, backgroundColor: colors.indigo600, color: semanticColors.onAccent, border: "none" }}>Print / save as PDF</button>
        <button type="button" data-testid="dn-doc-close" onClick={onClose}
          style={{ ...btn, backgroundColor: semanticColors.surface, color: semanticColors.textSecondary, border: `1px solid ${semanticColors.border}` }}>Close</button>
      </div>
      {error && <div role="alert" style={{ color: colors.red600, backgroundColor: semanticColors.surface, padding: spacing[12] }}>{error}</div>}
      {note && <DeliveryNoteDocument note={note} />}
    </div>,
    document.body,
  );
}
